import { useEffect, useRef, useState } from 'react';
import {
  EngineError,
  sendAction,
  listCaseFiles,
  uploadCaseFile,
  type CaseFile,
} from '../../api/engineDashboard';
import type { Party } from '../../types/case';
import type { SendActionResult } from '../../types/dashboard';

interface Props {
  token: string;
  caseId: string;
  parties: Party[];
  onSent: () => void;
  onUnauthorized: () => void;
  /** Party-tab Text/Email shortcuts (Danny, 2026-09-18): preselect the
      recipient + channel. nonce forces re-application on repeat clicks. */
  preselect?: { partyId: string; channel: 'sms' | 'email'; nonce: number } | null;
}

/**
 * Outbound composer. Recipient selection is REQUIRED and explicit — there
 * is deliberately no ambient "reply to the parties" send. Selecting several
 * parties sends each their own copy on their own private thread; the API
 * itself rejects sends without explicit recipient_party_ids.
 */
export function Composer({ token, caseId, parties, onSent, onUnauthorized, preselect }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [channel, setChannel] = useState<'sms' | 'email'>('sms');
  const [caseFiles, setCaseFiles] = useState<CaseFile[]>([]);
  const [attachIds, setAttachIds] = useState<Set<string>>(new Set());
  const [showAttach, setShowAttach] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!preselect) return;
    setSelected(new Set([preselect.partyId]));
    setChannel(preselect.channel);
  }, [preselect]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SendActionResult[] | null>(null);

  const toggle = (partyId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(partyId)) next.delete(partyId);
      else next.add(partyId);
      return next;
    });
  };

  const MAX_ATTACH = 3;

  // Upload files straight from the mediator's computer and attach them to
  // this send — no need to stage them in the Files panel first (Danny,
  // 2026-09-30: "upload and send ANY file to a party"). Each upload lands
  // as a case file (so it's also on the record) and is selected here.
  const uploadAndAttach = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    const room = MAX_ATTACH - attachIds.size;
    if (room <= 0) {
      setUploadError(`You can attach up to ${MAX_ATTACH} files.`);
      return;
    }
    const picked = [...files].slice(0, room);
    setUploading(true);
    try {
      for (const file of picked) {
        const uploaded = await uploadCaseFile(token, caseId, file);
        setCaseFiles((prev) => (prev.some((f) => f.id === uploaded.id) ? prev : [uploaded, ...prev]));
        setAttachIds((prev) => new Set(prev).add(uploaded.id));
      }
      setShowAttach(true);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    // Mediator-uploaded case files, attachable to an email (Danny,
    // 2026-09-30). Party-message attachments stream elsewhere; exclude.
    listCaseFiles(token, caseId)
      .then((r) => setCaseFiles(r.files.filter((f) => f.source !== 'party_message')))
      .catch(() => {});
  }, [token, caseId]);

  const partyName = (id: string) => parties.find((p) => p.id === id)?.name ?? id;
  const canSend = selected.size > 0 && body.trim().length > 0 && !sending;

  const send = async () => {
    setSending(true);
    setError(null);
    setResults(null);
    try {
      const res = await sendAction(token, caseId, {
        recipient_party_ids: [...selected],
        channel,
        body: body.trim(),
        ...(channel === 'email' && subject.trim() ? { subject: subject.trim() } : {}),
        ...(channel === 'email' && attachIds.size ? { attachment_file_ids: [...attachIds] } : {}),
      });
      setResults(res.results);
      if (res.failed === 0) {
        setBody('');
        setSubject('');
        setSelected(new Set());
        setAttachIds(new Set());
        setShowAttach(false);
        setUploadError(null);
      }
      onSent();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-t border-surface-border p-3">
      <div className="text-xs font-medium text-gray-600">
        Send to <span className="text-red-600">*</span>
        <span className="ml-1 font-normal text-gray-400">
          (each recipient gets their own private copy)
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {parties.map((p) => {
          const on = selected.has(p.id);
          const reachable = channel === 'sms' ? Boolean(p.phone) : Boolean(p.email);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(p.id)}
              title={reachable ? undefined : `No ${channel === 'sms' ? 'phone' : 'email'} on file`}
              className={[
                'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                on
                  ? 'border-brand bg-brand text-brand-fg'
                  : 'border-surface-border bg-white text-gray-700 hover:border-gray-300',
                reachable ? '' : 'opacity-50',
              ].join(' ')}
            >
              {on ? '✓ ' : ''}
              {p.name}
              <span className="ml-1 font-normal opacity-70">
                ({typeof p.metadata?.relationship === 'string' ? p.metadata.relationship : p.role})
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <select
          className="rounded-md border border-surface-border px-2 py-1"
          value={channel}
          onChange={(e) => setChannel(e.target.value as 'sms' | 'email')}
        >
          <option value="sms">SMS</option>
          <option value="email">Email</option>
        </select>
        {channel === 'email' && (
          <input
            type="text"
            placeholder="Subject (optional)"
            className="min-w-40 flex-1 rounded-md border border-surface-border px-2 py-1"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        )}
      </div>

      <textarea
        className="mt-2 w-full rounded-md border border-surface-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
        rows={3}
        placeholder="Write your message…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />

      {channel === 'email' && (
        <div className="mt-2 text-xs">
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => void uploadAndAttach(e.target.files)}
            />
            <button
              type="button"
              className="text-brand hover:underline disabled:opacity-50"
              disabled={uploading || attachIds.size >= MAX_ATTACH}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Uploading…' : '⬆ Upload & attach a file'}
            </button>
            {caseFiles.length > 0 && (
              <button type="button" className="text-brand hover:underline" onClick={() => setShowAttach((v) => !v)}>
                📎 From case files{attachIds.size ? ` (${attachIds.size})` : ''}
              </button>
            )}
          </div>
          {(showAttach || attachIds.size > 0) && caseFiles.length > 0 && (
            <div className="mt-1 space-y-1 rounded-md border border-surface-border p-2">
              {caseFiles.map((f) => (
                <label key={f.id} className="flex items-center gap-2 text-gray-700">
                  <input
                    type="checkbox"
                    checked={attachIds.has(f.id)}
                    disabled={!attachIds.has(f.id) && attachIds.size >= MAX_ATTACH}
                    onChange={() =>
                      setAttachIds((prev) => {
                        const n = new Set(prev);
                        if (n.has(f.id)) n.delete(f.id);
                        else n.add(f.id);
                        return n;
                      })
                    }
                  />
                  <span className="truncate">{f.label || f.file_name}</span>
                  <span className="shrink-0 text-gray-400">{Math.round(f.size_bytes / 1024)} KB</span>
                </label>
              ))}
            </div>
          )}
          <div className="mt-1 text-[11px] text-gray-400">
            Up to {MAX_ATTACH} files, 20 MB each, attached to the email. (SMS can’t carry files.)
          </div>
          {uploadError && <div className="mt-1 text-red-600">{uploadError}</div>}
        </div>
      )}

      <div className="mt-2 flex items-center gap-3">
        <button type="button" className="btn-primary" disabled={!canSend} onClick={() => void send()}>
          {sending
            ? 'Sending…'
            : selected.size > 1
              ? `Send separately to ${selected.size} parties`
              : 'Send'}
        </button>
        {selected.size === 0 && (
          <span className="text-xs text-gray-500">Pick at least one recipient.</span>
        )}
      </div>

      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}

      {results && (
        <ul className="mt-2 space-y-1 text-xs">
          {results.map((r) => (
            <li key={r.party_id} className={r.error ? 'text-red-700' : 'text-green-700'}>
              {partyName(r.party_id)}: {r.error ? `not sent — ${r.error}` : 'queued for delivery ✓'}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
